import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment-timezone';
import { get, orderBy, find, isEmpty } from 'lodash';
import classNames from 'classnames';
import { EventEmitter } from 'events';
import { prepareModelTimeForForm } from '../../../common/helpers/modelTime';
import {
  minServiceLenModel,
  barberOptionalFields,
} from '../../../common/helpers/modelSettings';
import {
  getBarber,
  updateBarber,
  saveBarberImage,
  getBarberAppointmentsForProfile,
  deleteBarber,
} from '../actions';
import { getShopUserServices } from '../../services/actions';
import BarberForm from '../formBarbers/barberForm';
import SimpleHeader from '../../headers/simpleHeader';
import {
  prepareSchedule,
  prepareDaysForForm,
  generateDays,
  onlyWholeDays,
} from '../../../common/helpers/schedules';
import {
  parseCustomerBookingInterval,
  parseMultiFieldValues,
} from '../../../common/helpers/times';
import { showNotification } from '../../../common/helpers/notifications';
import {
  canUserViewProfile,
  canUserModifyProfile,
  canUserViewSchedules,
} from '../../../common/helpers/users';
import * as selector from '../selector';
import withLoading from '../../../common/withLoading';
import { getIsRentalShop } from '../../../auth/selectors';
import withMinInterval from '../../../common/hoc/withMinInterval';
import confirmModal from '../../../common/confirmModal';
import { getAll } from '../selector';

const FormWithLoader = withLoading(BarberForm);

class BarberDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialValues: false,
      useShopServices: false,
    };
  }

  componentWillMount() {
    const {
      fetchBarber,
      fetchBarberAppointments,
      fetchShopServices,
    } = this.props;

    const barberId = get(this.props, 'params.barberId') || get(this.props, 'barberUser.id');
    const shop = this.props.shop || get(this.props, 'barberUser.shopId');

    fetchBarber(barberId).then(barberData => fetchShopServices(shop).then((services) => {
      const barber = this.decorateBarberData(barberData, services);
      return this.setState({ initialValues: barber, useShopServices: true });
    }));

    this.bindedFetchBarberAppointments = fetchBarberAppointments.bind(this, shop, barberId);
    EventEmitter.prototype.on('appointment-saved', this.bindedFetchBarberAppointments);
    EventEmitter.prototype.on('appointment-canceled', this.bindedFetchBarberAppointments);

    fetchBarberAppointments(shop, barberId);
  }

  componentWillUnmount() {
    EventEmitter.prototype.removeListener('appointment-saved', this.bindedFetchBarberAppointments);
    EventEmitter.prototype.removeListener('appointment-canceled', this.bindedFetchBarberAppointments);
  }

  saveBarber = (barberModel) => {
    const model = minServiceLenModel(barberModel, this.props.minInterval);
    let avatar = null;
    const { barber } = this.props;

    if (model.avatar instanceof File) {
      avatar = model.avatar;
      delete model.avatar;
    }

    return this.props
      .modifyBarber(barber.id, model)
      .then(() => {
        if (!avatar) {
          return Promise.resolve();
        }

        return this.props.saveBarberImage(barber.barberId, avatar);
      })
      .then(() => {
        const barberId = get(this.props, 'params.barberId') || get(this.props, 'barberUser.id');
        const shop = this.props.shop || get(this.props, 'barberUser.shopId');

        return Promise.all([this.props.fetchBarber(barberId), this.props.fetchShopServices(shop)]).then(
          ([barberData, services]) => {
            const updatedBarber = this.decorateBarberData(barberData, services);
            this.setState({
              initialValues: updatedBarber,
              useShopServices: true,
            });
          }
        );
      })
      .then(() => {
        showNotification({
          title: 'Success',
          level: 'success',
          message: 'Changes saved successfully',
        });
      });
  };

  deleteBarber = async (e) => {
    e.preventDefault();

    const {
      shop,
      removeBarber,
      params: { barberId },
    } = this.props;
    const confirmed = await confirmModal({
      type: 'danger',
      title: 'Delete barber',
      message: 'Are you sure you want to delete this barber?',
    });

    if (confirmed) {
      removeBarber(shop, barberId).then(() => this.props.router.push('/barbers'));
    }
  };

  decorateBarberData(barberData, services) {
    let barber = barberOptionalFields(barberData);
    barber.schedules = prepareSchedule(barber.schedules);
    barber.days_off = prepareDaysForForm(onlyWholeDays(barber.days_off));
    barber.days_on = prepareDaysForForm(barber.days_on);

    if (barber.photos && barber.photos.length) {
      barber.avatar = barber.photos[0].url;
    }

    if (!barber.schedules.current) {
      barber.schedules.current = {
        startDate: moment.utc().toDate(),
        workdays: generateDays(),
      };
    }

    if (!barber.schedules.future) {
      barber.schedules.future = {
        workdays: generateDays(),
      };
    }

    barber.populateUsername = barber.userEnabled && !isEmpty(barber.email) && barber.email === barber.username;

    barber.minServiceLen = parseCustomerBookingInterval(barber);
    barber = parseMultiFieldValues(barber);

    prepareModelTimeForForm(barber, 'notifications.reminder.time');
    prepareModelTimeForForm(barber, 'customerReminder.reminder.time');
    prepareModelTimeForForm(barber, 'customerReminder.barber_reminder.time');

    barber.services = orderBy(services, { order: 'asc' }).map((serviceData) => {
      const service = serviceData;
      service.id = serviceData.originalId;
      const assignment = find(service.assignments, { assigneeId: barber.id });
      service.visibleOnline = service.enabled;
      service.enabled = !!assignment;

      if (barber.defaultServiceId && assignment && assignment.assignmentId === barber.defaultServiceId) {
        barber.defaultServiceId = service.id;
      }

      return service;
    });

    return barber;
  }

  render() {
    const {
      initialValues,
      useShopServices,
    } = this.state;

    const {
      barberUser,
      permissions,
      isRentalShop,
      isBarbersLoaded,
    } = this.props;

    const formClass = classNames('u-margin-auto u-color-white', {
      'no-pointer-events': !canUserModifyProfile(permissions),
    });

    return (
      <div>
        <SimpleHeader heading="Barber details" />
        <div display-if={canUserViewProfile(permissions) || canUserViewSchedules(permissions)}>
          <FormWithLoader
            showLoading={!initialValues || !isBarbersLoaded}
            saveBarber={this.saveBarber}
            deleteBarber={this.deleteBarber}
            className={formClass}
            useShopServices={useShopServices}
            initialValues={initialValues}
            showUsername={!barberUser || isRentalShop}
            barberAppointments={this.props.barberAppointments}
            barberId={get(this.props, 'barber.id')}
            edit
          />
        </div>
        <p
          className="o-flexbox"
          display-if={!canUserViewProfile(permissions) && !canUserViewSchedules(permissions)}
        >
          You do not have permissions to view your profile.
        </p>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const barberId = get(props, 'params.barberId') || get(state, 'auth.barberUser.id');
  return {
    shop: state.auth.shop,
    user: state.auth.user,
    barberUser: state.auth.barberUser,
    barber: selector.getById(state, barberId),
    barberAppointments: state.barbers.barber_appointments,
    timezone: state.auth.timezone,
    permissions: state.auth.permissions,
    isRentalShop: getIsRentalShop(state),
    isBarbersLoaded: getAll(state).length > 0,
  };
};

const mapDispatchToProps = {
  modifyBarber: updateBarber,
  fetchBarber: getBarber,
  removeBarber: deleteBarber,
  fetchBarberAppointments: getBarberAppointmentsForProfile,
  fetchShopServices: getShopUserServices,
  saveBarberImage,
};

/* eslint-disable react/require-default-props */
BarberDetails.propTypes = {
  fetchBarber: PropTypes.func.isRequired,
  fetchBarberAppointments: PropTypes.func.isRequired,
  fetchShopServices: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  shop: PropTypes.string,
  barberUser: PropTypes.object,
  barber: PropTypes.object,
  modifyBarber: PropTypes.func.isRequired,
  saveBarberImage: PropTypes.func.isRequired,
  removeBarber: PropTypes.func.isRequired,
  barberAppointments: PropTypes.arrayOf(PropTypes.object).isRequired,
  router: PropTypes.object,
  permissions: PropTypes.object.isRequired,
  isRentalShop: PropTypes.bool,
  minInterval: PropTypes.number.isRequired,
  isBarbersLoaded: PropTypes.bool,
  user: PropTypes.object.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(withMinInterval(BarberDetails)));
